import { createEmbeddingContext, type EmbeddingContext } from 'amazon-quicksight-embedding-sdk';
import { useEffect, useRef, useState } from 'react';

import { useAnalyticsEmbedUrl } from '../../api/analytics.js';
import { WarningIcon } from '../../components/icons/WarningIcon.js';
import { Layout } from '../../components/Layout.js';
import { usePageTitle } from '../../hooks/usePageTitle.js';
import './analytics.css';

const HEADING_ID = 'analytics-heading';

/**
 * The team analytics dashboard, embedded.
 *
 * Four things here exist because QuickSight does not do them for us.
 *
 * **One URL per mount.** The embed URL is single-use with a five-minute
 * redemption window, so React's double-invoked effects in StrictMode would
 * redeem it twice and the second attempt fails in a way that reads as a
 * permissions bug. A mount guard is the fix; disabling StrictMode would only hide
 * the same defect until React 19's concurrent re-mounting surfaced it again.
 *
 * **A self-imposed session clock.** The embedding SDK fires no event when a
 * session expires — an AWS issue open since 2023, confirmed by a maintainer — and
 * the dashboard simply spins forever. So the lifetime is tracked here and the
 * frame re-embedded shortly before it lapses.
 *
 * **Announcements that do not stack.** A real failure is a `role="alert"`, which
 * is an assertive live region in its own right; the polite region below stays
 * silent for that case so one event is not announced twice in two wordings. The
 * no-scope note is the opposite — `role="note"` carries no live semantics at all,
 * so its polite announcement is the only thing a screen reader hears.
 *
 * **Accessibility we own, and accessibility we do not.** The dashboard is a
 * cross-origin iframe: `axe-core` cannot see into it, so the CI a11y gate will
 * pass regardless of what QuickSight renders inside, and neither its markup nor
 * its contrast nor its focus order is ours to change. That is a named exception,
 * not an implied pass. What is ours — the surrounding chrome, an accessible name
 * on the frame, and somewhere for focus to land when the reload control unmounts
 * itself — is done here.
 */
export function AnalyticsPage() {
  usePageTitle('Team analytics');

  const containerRef = useRef<HTMLDivElement>(null);
  const mountedUrlRef = useRef<string | null>(null);
  // One EmbeddingContext (and its zero-pixel iframe, appended to <body> by the
  // SDK) per component lifetime — memoized here and reused across renewals and
  // reloads instead of calling createEmbeddingContext() on every effect run.
  const embeddingContextRef = useRef<Promise<EmbeddingContext> | null>(null);
  const announcedReadyRef = useRef(false);
  const [status, setStatus] = useState<'idle' | 'loading' | 'ready' | 'failed'>('idle');
  const [announceReady, setAnnounceReady] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  const query = useAnalyticsEmbedUrl(true);

  useEffect(() => {
    const container = containerRef.current;
    const data = query.data;
    if (!container || !data || data.status !== 'ready') return;
    // Guard against a second redemption of the same single-use token.
    if (mountedUrlRef.current === data.embedUrl) return;

    let disposed = false;
    let succeeded = false;
    let renewTimer: ReturnType<typeof setTimeout> | undefined;
    setStatus('loading');

    void (async () => {
      try {
        embeddingContextRef.current ??= createEmbeddingContext();
        const context = await embeddingContextRef.current;
        if (disposed) return;
        await context.embedDashboard({
          url: data.embedUrl,
          container,
          height: '100%',
          width: '100%',
        });
        if (disposed) return;
        // The embedding SDK's own FrameOptions has no `title` field and never
        // sets one on the <iframe> it creates — the wrapping div's aria-label
        // above names the landmark region, but that name does not propagate
        // to the child frame itself, which a screen reader's frame list still
        // reports as unlabeled. Named here, on the actual DOM node, once per
        // successful embed (a fresh iframe can land on each renewal).
        container.querySelector('iframe')?.setAttribute('title', 'Team analytics dashboard');
        succeeded = true;
        // Commit the guard only after a successful embed. Setting this
        // earlier (before the async work resolves) meant a torn-down attempt
        // — StrictMode's synchronous mount/cleanup/remount, or a real
        // unmount — left it permanently pointed at a URL that was never
        // actually redeemed, with no self-recovery.
        mountedUrlRef.current = data.embedUrl;
        setStatus('ready');
        // Announce the first successful load only. A silent background renewal
        // re-runs this path, and re-announcing would interrupt someone mid-read
        // for something they did not do.
        if (!announcedReadyRef.current) {
          announcedReadyRef.current = true;
          setAnnounceReady(true);
        }

        // Re-embed a minute before the session lapses, because nothing will tell
        // us when it does.
        const renewAfterMs = Math.max(data.sessionLifetimeMinutes - 1, 1) * 60_000;
        renewTimer = setTimeout(() => {
          mountedUrlRef.current = null;
          setReloadKey((k) => k + 1);
        }, renewAfterMs);
      } catch {
        if (!disposed) setStatus('failed');
      }
    })();

    return () => {
      disposed = true;
      if (!succeeded) mountedUrlRef.current = null;
      if (renewTimer) clearTimeout(renewTimer);
    };
    // reloadKey intentionally excluded: it's only consumed by the refetch
    // effect below. Refetching is async, so including it here would re-run
    // this effect on stale query.data before the refetch resolves, redeeming
    // an already-spent token a second time.
  }, [query.data]);

  const { refetch } = query;
  useEffect(() => {
    // Only on an explicit reload or a renewal: a fresh token per attempt, since
    // the previous one is spent whether it rendered or not.
    if (reloadKey > 0) void refetch();
  }, [reloadKey, refetch]);

  const noScope = query.data?.status === 'no-scope';
  const failed = status === 'failed' || query.isError;

  return (
    <Layout>
      <div className="analytics-page">
        {/* Focusable so the reload control has somewhere to send focus when it
            removes itself from the DOM — same approach as the timesheet page. */}
        <h1 id={HEADING_ID} tabIndex={-1}>
          Team analytics
        </h1>

        {/* Mounted unconditionally with toggling content: a live region that
            appears in the same commit as its text is announced unreliably in
            VoiceOver. Silent for `failed`, because the role="alert" below is
            already an assertive live region and owns that announcement. */}
        <div aria-live="polite" className="sr-only">
          {failed
            ? ''
            : noScope
              ? 'You do not have analytics access for any team yet.'
              : announceReady
                ? 'Analytics dashboard loaded.'
                : ''}
        </div>

        {noScope && (
          <p className="analytics-page__empty" role="note">
            Analytics are scoped to the teams you approve, and you aren&rsquo;t an approver on any
            team yet — so there&rsquo;s nothing to show. Ask an administrator to add you as an
            approver.
          </p>
        )}

        {failed && !noScope && (
          // `.error-banner` with an icon, not the pale informational callout used
          // for the no-scope note. REQUIREMENTS "Branding" requires error states
          // to pair Bluebonnet with a clear glyph so colour is never the sole
          // signal — and a genuine failure should not look identical to the benign
          // case of approving no teams.
          <div className="error-banner" role="alert">
            <WarningIcon className="error-banner__icon" />
            <span>The dashboard couldn&rsquo;t be loaded.</span>
            <button
              type="button"
              className="btn btn--secondary"
              onClick={() => {
                // Before the state change unmounts this button: otherwise focus
                // falls to <body> and a keyboard user loses their place entirely.
                document.getElementById(HEADING_ID)?.focus();
                mountedUrlRef.current = null;
                setStatus('idle');
                setReloadKey((k) => k + 1);
              }}
            >
              Reload analytics
            </button>
          </div>
        )}

        {!noScope && !failed && (
          <div
            ref={containerRef}
            className="analytics-page__frame"
            // The frame's contents are cross-origin and not ours; this names the
            // region for anyone navigating by landmark.
            role="region"
            aria-label="Team analytics dashboard"
            aria-busy={status !== 'ready'}
          />
        )}
      </div>
    </Layout>
  );
}
